import { Location } from "@angular/common";
import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import {
  Email,
  InvoiceHeader,
  JoHeader,
  Manifest,
  ManifestGroup,
} from "app/model/entities/entity-model";
import { DateFns } from "app/shared/date-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { EmailService } from "app/shared/email.service";
import { PrintFns } from "app/shared/print-fns";
import { UtilFns } from "app/shared/util-fns";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { ManifestPrintDialogComponent } from "./manifest-print-dialog.component";

interface FlatInvoice {
  empName: string;
  invoiceId: string;
  invoiceDate: string;
  subTotalAmt: string;
  freightAmt: string;
  taxAmt: string;
  prepaymentAmt: string;
  paymentAmt: string;
  orderHandlingAmt: string;
  totalAmt: string;
}

interface ManifestTotals {
  subTotalAmt: number;
  freightAmt: number;
  orderHandlingAmt: number;
  taxAmt: number;
  prepaymentAmt: number;
  paymentAmt: number;
  totalAmt: number;
}

interface InvoiceGroup {
  programUserGroupName: string;
  invoices: FlatInvoice[];
  totals: ManifestTotals;
}

@Component({
  selector: "app-printable-manifest",
  templateUrl: "./printable-manifest.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PrintableManifestComponent
  extends DomainBaseComponent
  implements OnDestroy
{
  @ViewChild("printableArea") printElementRef: ElementRef;
  manifestId: number;
  manifest: Manifest;
  sentDate: Date;
  startDate: Date;
  endDate: Date;
  // only avail if called with manifestGroupId specified;
  manifestGroupId: number;
  manifestGroup: ManifestGroup;

  invoiceHeaders: InvoiceHeader[];
  flatInvoices: FlatInvoice[];
  // subtotaled by program user group only when the manifest spans more than one group
  invoiceGroups: InvoiceGroup[];
  isGroupedByProgramUserGroup = false;
  joHeader: JoHeader;
  email: Email;
  totals: ManifestTotals = this.createEmptyTotals();

  canSendInvoice = false;
  isPosted = false;
  isPreview = false;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    private emailService: EmailService,
    private ngLocation: Location,
    private elementRef: ElementRef
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  ngOnDestroy(): void {
    // PrintFns.setPrintFn(null);
  }

  async updateFromContext() {
    // This component can get called with either a manifestId or a manifestGroupId
    // if called with a manifestId then the goal is to display the 'next pending' manifest 'group' to send
    // if called with a manifestGroupId then the goal is to display a previously sent manifestGroup.
    const params = this.route.snapshot.params;
    let temp = params["manifestId"];
    this.manifestId = temp != null ? parseInt(temp, 10) : null;
    temp = params["manifestGroupId"];
    this.manifestGroupId = temp != null ? parseInt(temp, 10) : null;

    this.isPreview = this.route.snapshot.queryParams["preview"] === "true";

    const user = this.domainService.authService.getUser();
    this.canSendInvoice = user.isAccountingAdmin;

    await this.dbQueryService.cacheAll();

    if (this.manifestGroupId) {
      this.invoiceHeaders =
        await this.dbQueryService.getManifestInvoicesForManifestGroup(
          this.manifestGroupId
        );
    } else {
      this.invoiceHeaders =
        await this.dbQueryService.getManifestInvoicesPostedButUnsent(
          this.manifestId
        );
    }

    if (this.invoiceHeaders.length == 0) {
      this.manifest = null;
      this.isPageReady = true;
      return;
    }

    if (this.manifestGroupId != null) {
      this.manifestGroup =
        this.invoiceHeaders[0].manifestGroupInvoices[0].manifestGroup;
      this.manifestId = this.manifestGroup.manifestId;
      this.isPosted = true;
    }

    this.manifest = await this.dbQueryService.getManifest(this.manifestId);
    if (this.manifest == null) {
      this.isPageReady = true;
      return;
    }

    const fmtCurrency = this.UtilFns.fmtCurrency;
    const fmtDate = DateFns.fmtDate4Short;
    this.totals = this.createEmptyTotals();
    const groupsByName = new Map<string, InvoiceGroup>();
    this.flatInvoices = this.invoiceHeaders.map((i) => {
      const rowTotals: ManifestTotals = {
        subTotalAmt: i.subtotalAmt ?? 0,
        freightAmt: i.freightAmt ?? 0,
        orderHandlingAmt: i.orderHandlingAmt ?? 0,
        taxAmt: i.taxAmt ?? 0,
        prepaymentAmt: i.prepaymentAmt ?? 0,
        paymentAmt: i.pCardAmt ?? 0,
        totalAmt: 0,
      };
      rowTotals.totalAmt =
        rowTotals.subTotalAmt +
        rowTotals.freightAmt +
        rowTotals.orderHandlingAmt +
        rowTotals.taxAmt -
        rowTotals.prepaymentAmt -
        rowTotals.paymentAmt;

      if (!this.joHeader && i.joHeader.billingAddress1) {
        this.joHeader = i.joHeader;
      }
      const flatInvoice: FlatInvoice = {
        empName: this.getEmpName(i.joHeader),
        invoiceId: i.id,
        invoiceDate: fmtDate(i.invoiceDate),
        subTotalAmt: fmtCurrency(rowTotals.subTotalAmt),
        freightAmt: fmtCurrency(rowTotals.freightAmt),
        taxAmt: fmtCurrency(rowTotals.taxAmt),
        orderHandlingAmt: fmtCurrency(rowTotals.orderHandlingAmt),
        prepaymentAmt: "(" + fmtCurrency(rowTotals.prepaymentAmt) + ")",
        paymentAmt: "(" + fmtCurrency(rowTotals.paymentAmt) + ")",
        totalAmt: fmtCurrency(rowTotals.totalAmt),
      };

      const groupName = i.joHeader.programUserGroupName?.trim() ?? "";
      let group = groupsByName.get(groupName);
      if (!group) {
        group = { programUserGroupName: groupName, invoices: [], totals: this.createEmptyTotals() };
        groupsByName.set(groupName, group);
      }
      group.invoices.push(flatInvoice);
      this.addTotals(group.totals, rowTotals);
      this.addTotals(this.totals, rowTotals);

      return flatInvoice;
    });

    // groups without a name sort last
    this.invoiceGroups = _.sortBy([...groupsByName.values()], [
      (g) => (g.programUserGroupName ? 0 : 1),
      (g) => g.programUserGroupName.toLowerCase(),
    ]);
    this.isGroupedByProgramUserGroup = this.invoiceGroups.length > 1;

    this.setDateRange();

    this.isPageReady = true;

    setTimeout(() => {
      // if printing or headless browser, use print view
      const mediaQueryList = window.matchMedia("print");
      if (
        mediaQueryList.matches ||
        window.navigator.userAgent.includes("Headless")
      ) {
        this.preparePrint();
      } else {
        // get email status
        this.getManifestEmailStatus();
      }
    }, 0);
  }

  private createEmptyTotals(): ManifestTotals {
    return {
      subTotalAmt: 0,
      freightAmt: 0,
      orderHandlingAmt: 0,
      taxAmt: 0,
      prepaymentAmt: 0,
      paymentAmt: 0,
      totalAmt: 0,
    };
  }

  private addTotals(target: ManifestTotals, source: ManifestTotals) {
    target.subTotalAmt += source.subTotalAmt;
    target.freightAmt += source.freightAmt;
    target.orderHandlingAmt += source.orderHandlingAmt;
    target.taxAmt += source.taxAmt;
    target.prepaymentAmt += source.prepaymentAmt;
    target.paymentAmt += source.paymentAmt;
    target.totalAmt += source.totalAmt;
  }

  private getEmpName(jo: JoHeader) {
    if (jo.empLastName || jo.empFirstName) {
      return (
        (jo.empLastName ? jo.empLastName + ", " : "") + (jo.empFirstName || "")
      );
    }
    return jo.billingName || jo.shippingName;
  }

  // set startDate and endDate.  Called only after invoiceHeaders are loaded.
  private async setDateRange() {
    const sentGroups = _.sortBy(
      this.manifest.manifestGroups.filter(mg => mg.sentDate != null),
      (mg) => mg.sentDate
    );
    if (this.manifestGroupId != null) {
      const ix = sentGroups.findIndex(
        (mg) => mg.id == this.manifestGroupId
      );
      if (ix >= 0) {
        this.endDate = sentGroups[ix].sentDate;
        if (ix > 0) {
          this.startDate = sentGroups[ix - 1].sentDate;
        } else {
          this.startDate = null;
        }
      } else {
        // Current group hasn't been sent yet — use today
        this.endDate = DateFns.startOfDay(new Date());
        if (sentGroups.length > 0) {
          this.startDate = sentGroups[sentGroups.length - 1].sentDate;
        }
      }
    } else {
      const today = DateFns.startOfDay(new Date());
      this.endDate = today;
      if (sentGroups.length > 0) {
        this.startDate = sentGroups[sentGroups.length - 1].sentDate;
      }
    }
  }

  async showPrintDialog() {
    if (!this.canSendInvoice) {
      await this.dialogService.showOkMessage(
        "Not authorized",
        "This is an accounting function that you are not currently authorized for."
      );
      return false;
    }

    const r = await DialogBaseComponent.open(
      ManifestPrintDialogComponent,
      this,
      {
        manifest: this.manifest,
        manifestGroup: this.manifestGroup,
        canPreview: false,
      }
    );

    this.getManifestEmailStatus();
    return <boolean>r;
  }

  getBillingAddress() {
    if (this.joHeader) {
      return UtilFns.getAddress(this.joHeader, "billing", false, false);
    }
    return "";
  }

  getUnitecAddress() {
    return this.dbQueryService.getUnitecAddressAsString();
  }

  preparePrint() {
    let el: HTMLElement =
      this.printElementRef && this.printElementRef.nativeElement;
    if (el == null) {
      el = this.elementRef && this.elementRef.nativeElement;
    }
    if (!el) {
      return;
    }
    PrintFns.printElement(el);
  }

  getManifestEmailStatus() {
    if (this.manifestGroupId) {
      this.emailService
        .getManifestEmailStatus(this.manifestGroupId)
        .then((e) => (this.email = e));
    }
  }

  goBack() {
    this.ngLocation.back();
  }
}
